import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Award, ChevronLeft, ChevronRight, Headphones, KeyRound, Settings2, Zap } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

const TRUST_ITEMS = [
  { icon: Zap, title: "Livraison", subtitle: "instantanée" },
  { icon: KeyRound, title: "Clés officielles", subtitle: "100% vérifiées" },
  { icon: Headphones, title: "Support client", subtitle: "24/7" },
  { icon: Award, title: "Garantie", subtitle: "satisfait ou remboursé" },
];

function HeroMediaLink({ link, testId }) {
  if (!link) return null;
  const external = /^https?:\/\//i.test(link);
  const cls = "absolute inset-0 z-[5] block cursor-pointer";
  if (external) return <a href={link} target="_blank" rel="noopener noreferrer" data-testid={testId} className={cls} aria-label="Ouvrir le lien" />;
  return <Link to={link} data-testid={testId} className={cls} aria-label="Ouvrir le lien" />;
}

export function HeroSlider({ slides = [] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [trust, setTrust] = useState(null);
  const count = slides.length;

  useEffect(() => {
    let active = true;
    api.get("/settings/hero-trust")
      .then(({ data }) => active && setTrust(data))
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const go = useCallback((next) => setIndex(((next % count) + count) % count), [count]);

  useEffect(() => {
    if (paused || count < 2) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), 7000);
    return () => clearInterval(timer);
  }, [paused, count]);

  if (!count) return <div className="hero-shell h-[520px] w-full" />;
  const slide = slides[index];
  const isBg = slide.layout === "background";
  const showText = !isBg || slide.show_text !== false;
  const showButton = slide.show_button !== false;
  const trustEnabled = trust ? trust.enabled !== false : true;
  const trustItems = (trust?.items?.length ? trust.items : TRUST_ITEMS).map((item, i) => ({
    ...item,
    icon: TRUST_ITEMS[i % TRUST_ITEMS.length].icon,
  }));

  return (
    <section
      data-testid="hero-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="hero-shell relative w-full overflow-hidden"
    >
      {isBg && (slide.video_url || slide.image_url) && (
        <>
          {slide.video_url ? (
            <video
              src={fileUrl(slide.video_url)}
              autoPlay
              muted
              loop
              playsInline
              data-testid="hero-bg-video"
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <img
              src={fileUrl(slide.image_url)}
              alt=""
              data-testid="hero-bg-image"
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}
          {slide.darken && (
            <div className="absolute inset-0 bg-gradient-to-r from-[#020617]/80 via-[#020617]/50 to-[#020617]/25" />
          )}
          <HeroMediaLink link={slide.image_link} testId="hero-bg-link" />
        </>
      )}
      {!isBg && <div className="hero-grid-lines pointer-events-none absolute inset-0 opacity-70" />}
      {!isBg && <div className="pointer-events-none absolute -left-24 top-1/3 h-72 w-72 rounded-full bg-brand/25 blur-[120px]" />}

      <div className={`container-it relative z-10 ${isBg && slide.image_link ? "pointer-events-none" : ""}`}>
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className={isBg ? "flex min-h-[540px] items-center py-14" : "grid min-h-[540px] items-center gap-10 py-14 lg:grid-cols-[1.05fr_1fr]"}
          >
            {(showText || showButton) && (
            <div>
              {showText && (
              <>
              {slide.badge && (
                <span
                  data-testid="hero-badge"
                  className="mb-5 inline-flex items-center gap-2 rounded-full bg-rose-500 px-3.5 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-white"
                >
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
                  {slide.badge}
                </span>
              )}
              <h1 className="font-display text-4xl font-extrabold leading-[1.05] text-white sm:text-5xl xl:text-[3.6rem]">
                {slide.title}{" "}
                <span className="text-brand [text-shadow:0_0_40px_rgba(0,82,255,0.8)]">{slide.highlight}</span>
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">{slide.text}</p>

              {trustEnabled && (
              <div className="mt-9 grid max-w-2xl grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4" data-testid="hero-trust-row">
                {trustItems.map(({ icon: Icon, title, subtitle }) => (
                  <div key={title} className="flex items-start gap-2">
                    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-white" strokeWidth={1.8} />
                    <span className="text-[12px] leading-tight text-slate-400">
                      <span className="block font-bold text-white">{title}</span>
                      {subtitle}
                    </span>
                  </div>
                ))}
              </div>
              )}
              </>
              )}

              {showButton && (
              <div className={`pointer-events-auto flex flex-wrap items-center gap-3 ${showText ? "mt-10" : ""}`}>
                <Link
                  to={slide.button_link || "/boutique"}
                  data-testid="hero-primary-cta"
                  className="btn-glow inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-[15px] font-bold text-white transition-transform duration-300 hover:scale-[1.03] active:scale-95"
                >
                  {slide.button_text || "Découvrir la boutique"}
                  <ChevronRight className="h-4 w-4" />
                </Link>
                {slide.secondary_text && (
                  <Link
                    to={slide.secondary_link || "/services"}
                    data-testid="hero-secondary-cta"
                    className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-[15px] font-bold text-white transition-colors duration-300 hover:border-white hover:bg-white/10"
                  >
                    <Settings2 className="h-4 w-4" />
                    {slide.secondary_text}
                  </Link>
                )}
              </div>
              )}
            </div>
            )}

            {!isBg && (
            <div className="relative flex h-[380px] items-center justify-center">
              <div className="platform-glow absolute bottom-4 h-40 w-full max-w-[520px]" />
              {slide.image_url ? (
                <div className="relative">
                  <img
                    src={slide.image_url}
                    alt={slide.title}
                    className="max-h-[340px] w-auto rounded-2xl object-contain drop-shadow-[0_30px_60px_rgba(0,82,255,0.45)]"
                  />
                </div>
              ) : (
                <div className="relative flex items-end justify-center gap-3">
                  {(slide.products || []).slice(0, 5).map((src, i) => (
                    <img
                      key={src}
                      src={src}
                      alt=""
                      loading="lazy"
                      className="h-auto w-[110px] rounded-xl bg-white object-contain p-2 shadow-[0_25px_60px_rgba(0,82,255,0.35)] xl:w-[130px]"
                      style={{ transform: `translateY(${Math.abs(i - 2) * 14}px)` }}
                    />
                  ))}
                </div>
              )}
              <div className="absolute bottom-0 h-1.5 w-[60%] rounded-full bg-cyan-400/80 blur-[3px]" />
            </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="pointer-events-auto absolute bottom-8 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {slides.map((s, i) => (
            <button
              key={s.id || i}
              data-testid={`hero-dot-${i}`}
              aria-label={`Slide ${i + 1}`}
              onClick={() => go(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === index ? "w-9 bg-brand" : "w-2.5 bg-white/30 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
      </div>

      <button
        data-testid="hero-prev"
        aria-label="Slide précédente"
        onClick={() => go(index - 1)}
        className="absolute left-4 top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white backdrop-blur-md transition-colors duration-200 hover:bg-white/15 xl:flex"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        data-testid="hero-next"
        aria-label="Slide suivante"
        onClick={() => go(index + 1)}
        className="absolute right-4 top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white backdrop-blur-md transition-colors duration-200 hover:bg-white/15 xl:flex"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
    </section>
  );
}

export default HeroSlider;
